<template>
	<div v-if="reportData && project">
		<final-report-header
			:project="project"
			:report-data="reportData"/>

		<v-alert :value="project.projectStatus === 'REQUESTED_CHANGES'
			|| project.projectStatus === 'POST_CORRECTION_MAIN'" type="warning" class="ma-3">
			Projekt jest obecnie w trakcie poprawek. Zakończ je i odeślij projekt zanim zaczniesz pisać sprawozdanie.
			W przeciwnym razie treść sprawozdania zostanie utracona.
		</v-alert>

		<v-divider class="ma-5"/>

		<div class="mt-3 mb-3">
			<h2 style="margin-left: 20px">{{ configuration.form.sections[0].name }} </h2>
			<vue-form-generator
				name="generator"
				ref="vfg"
				:model="reportDataModel"
				:options="formGenerator.formOptions"
				:schema="formGenerator.currentSchema"
				v-if="viewData.showGenerator">
			</vue-form-generator>
		</div>

		<v-divider class="ma-5"/>

		<div v-if="project.financialDocumentsIncluded">
			<div style="page-break-inside: avoid">
				<h2 style="margin-left: 20px" class="mb-1">Sprawozdanie z wykonania wydatków</h2>
				<final-report-financial-table
					ref="reportFinancialTable"
					:project-data="reportData.fromProject"
					:invoices-data="reportData.fromDocuments.invoices"
					:readonly="readonly"
				/>
			</div>

			<v-divider class="ma-5"/>

			<final-report-invoices
				ref="finalReportInvoices"
				:invoices="reportData.fromDocuments.invoices"
				:show-invoices-warning="showInvoicesWarning"
				:donation="invoicesWarningDonation"
				:project-start-date="project.projectStartDate"
				:project-end-date="project.projectEndDate"
				:terms="terms"
			/>

			<v-divider class="ma-5"/>
		</div>

		<final-report-volunteers
			:volunteers="reportData.fromProject.volunteers"
			:readonly="readonly"
		/>

		<v-divider class="ma-5"/>

		<div>
			<v-layout ml-5 mr-5 mb-5>
				<v-btn v-if="!readonly" @click="validateAndShowDialog" color="success"
					class="no-print">
					<v-icon class="mr-2 white--text">send</v-icon>
					Wyślij
				</v-btn>

				<v-btn v-if="!readonly" @click="saveReport" color="info"
					class="no-print">
					<v-icon class="mr-2 white--text">save</v-icon>
					Zapisz sprawozdanie
				</v-btn>

				<v-btn v-if="!readonly" @click="viewReport" color="primary"
					class="no-print">
					<v-icon class="mr-2 white--text">visibility</v-icon>
					Podgląd sprawozdania
				</v-btn>

				<v-spacer></v-spacer>

				<v-btn v-if="showTablesExportButton && project.financialDocumentsIncluded"
					@click="exportCosts"
					color="info"
					class="no-print">
					<v-icon class="mr-2 white--text">cloud_download</v-icon>
					Eksportuj tabelę kosztów
				</v-btn>
			</v-layout>
		</div>

		<project-final-report-confirmation-dialog :action="send" :dialog-model="viewData.sendReportDialog"
			@close-confirmation="viewData.sendReportDialog = false"
			:show-bidder-changes-checkbox="subBidderType === 'SUBSIDIARY'"
			action-label="Wyślij" content="Przed wysłaniem sprawozdania upewnij się, że jest poprawnie wypełnione.
				Po wysłaniu nie będzie już możliwości edycji!" header="Wysyłka raportu" icon="send" :loading="loading"/>

		<v-alert :value="roles.isSubBidder()" type="warning" class="ma-5">
			Sprawozdanie wymaga potwierdzenia przez głównego oferenta. Oferent główny otrzyma prośbę
			o potwierdzenie sprawozdania po jego wysłaniu.
		</v-alert>
	</div>
</template>

<script>
import FinalReportFinancialTable from "./FinalReportFinancialTable";
import FinalReportVolunteers from "./FinalReportVolunteers";
import FinalReportHeader from "./FinalReportHeader";
import {FinalReport} from "@/api/generated/FinalReport";
import {Api} from "@/api";
import {genericFormUtil} from "@/utils/genericFormUtil";
import ProjectFinalReportConfirmationDialog from "../contest/components/ProjectFinalReportConfirmationDialog";
import {Dates} from "@/utils/Dates";
import {Files} from "@/utils/Files";
import FinalReportInvoices from "./FinalReportInvoices";
import {Numbers} from "@/utils/Numbers";
import {FundingTypes} from "./Utils/FinalReportFundingTypes";
import {Project} from "@/api/generated/Project";
import {SubBidder} from "@/api/generated/SubBidder";

export default {
  name: "FinalReportComponent",
  components: {
    FinalReportInvoices,
    ProjectFinalReportConfirmationDialog,
    FinalReportHeader, FinalReportVolunteers, FinalReportFinancialTable,
  },

  mixins: [Numbers],

  watch: {
    reportDataModel: {
      handler() {
        this.applyGenericForm(this.configuration.form);
      },
      deep: true,
    },

    reportData: {
      handler() {
        this.$nextTick(() => {
          if (this.$refs.reportFinancialTable) {
            this.showInvoicesWarning = parseFloat(this.$refs.reportFinancialTable.sumOfCostsFromTypesFromDocument([FundingTypes.DOTATION]).toFixed(2)) >
                parseFloat(this.$refs.reportFinancialTable.sumOfCostsFromTypesFromProject([FundingTypes.DOTATION]).toFixed(2));
            this.invoicesWarningDonation = this.$refs.reportFinancialTable.sumOfCostsFromTypesFromProject([FundingTypes.DOTATION]);
          }
        });
      },
    },
  },

  props: {
    projectId: {},
    readonly: {},
    showTablesExportButton: {type: Boolean, default: false},
  },

  data() {
    return {
      project: null,

      terms: {},

      userId: null,

      reportData: null,

      reportDataModel: undefined,

      showInvoicesWarning: false,

      invoicesWarningDonation: null,

      subBidderType: "",

      configuration: {
        form: "",
      },

      viewData: {
        showGenerator: false,
        sendReportDialog: false,
      },

      formGenerator: {
        currentSchema: {},
        currentModel: {},
        formOptions: {
          validateAfterLoad: false,
          validateAfterChanged: true,
          validateAsync: true,
        },
        currentConditionalHashes: [],
      },
      loading: false

    };
  },

  methods: {
    refresh() {
      this.loadData();
    },

    async loadData() {
      await Api.syncGet(this, "/final-report/configuration?projectId=".concat(this.projectId), successResponse => {
        this.configuration.form = genericFormUtil.decodeSchema(successResponse.schema);

        if (!this.configuration.form) {
          this.$messagesBus.$emit("error", "Niezdefiniowany formularz generyczny");
          return;
        }

        this.formGenerator.currentSchema = genericFormUtil.getSectionSchemaFromSchema(this.configuration.form, 0);
      });

      Api.get(this, "/user/user-info", successResponse => {
        this.userId = successResponse.id;
      }, errorResponse => {
        this.$messagesBus.$emit("error", "Nie udało się załadować danych!", errorResponse);
      });

      FinalReport.getData(this, this.projectId, success => {
        this.reportData = success;
        if (this.reportData.filledForm === null) {
          this.reportDataModel = genericFormUtil.buildModelFromSchema(this.configuration.form).sections[0].fields;
        } else {
          this.reportDataModel = genericFormUtil.decodeSchema(this.reportData.filledForm);
        }
        this.applyGenericForm(this.configuration.form);
        this.viewData.showGenerator = true;
      });

      Project.getPublicBasicData(this, this.$route.params.projectId, projectBasicData => {
          this.project = projectBasicData;
        FinalReport.getSendAndApprovalDates(this, this.$route.params.projectId, dates => {
          this.sendDate = dates.sendDate;
          this.approvalDate = dates.approvalDate;
        });
      });

      Project.getTerms(this, this.$route.params.projectId, successResponse => {
        this.terms = successResponse;
      }, errorResponse => {
        this.$messagesBus.$emit("error", "Nie udało się załadować terminów", errorResponse);
      });

      if (this.roles.isSubBidder()) {
        SubBidder.getSubBidderType(this,
            success => {
              this.subBidderType = success.subBidderType;
            });
      }
    },

    async send(allowMainBidderChanges) {
      if(!this.loading){
      this.loading = true;
      let validationPromise = await this.$refs.vfg.validate();
      if (validationPromise.length > 0) {
        this.loading = false;
        return;
      }
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.reportData.filledForm = genericFormUtil.encodeSchema(this.reportDataModel);
          if (this.roles.isBidder()) {
            this.sendAsBidder();
          } else {
            this.sendAsSubBidder(allowMainBidderChanges);
          }
          this.viewData.sendReportDialog = false;
        } else {
          this.$messagesBus.$emit("error", "Uzupełnij poprawnie dane!");
        }
        this.loading = false;
      });
    }
    },

    async sendAsBidder() {
      FinalReport.postData(this, this.projectId, this.reportData, () => {
        FinalReport.postSubmit(this, this.projectId, successResponse => {
          this.$messagesBus.$emit("success", "Raport został wysłany!", successResponse);
          this.$router.push("/app/project/bidder-project-list/".concat(this.userId));
        }, errorResponse => {
          this.$messagesBus.$emit("error", "Nie udało się wysłać raportu", errorResponse);
        });
      });
    },

    async sendAsSubBidder(allowMainBidderChanges) {
      FinalReport.putSendForAcceptance(this, this.projectId, allowMainBidderChanges, this.reportData, successResponse => {
        this.$messagesBus.$emit("success", "Raport został wysłany!", successResponse);
        this.$router.push("/app/project/bidder-project-list/".concat(this.userId));
      }, errorResponse => {
        this.$messagesBus.$emit("error", "Nie udało się wysłać raportu", errorResponse);
      });
    },

    async validateAndShowDialog() {
      let validatePromise = await this.$refs.vfg.validate();
      if (validatePromise.length > 0) {
        this.$messagesBus.$emit("error", "Uzupełnij poprawnie dane formularza");
        return;
      }
      this.$validator.validateAll().then((result) => {
        if (!result) {
          this.$messagesBus.$emit("error", "Uzupałnij dane opinii");
        } else {
          this.viewData.sendReportDialog = true;
        }
      });
    },

    saveReport() {
      this.reportData.filledForm = genericFormUtil.encodeSchema(this.reportDataModel);
      FinalReport.postData(this, this.projectId, this.reportData, successResponse => {
        this.$messagesBus.$emit("success", "Pomyślnie zapisano raport", successResponse);
        this.loadData();
      }, errorResponse => {
        this.$messagesBus.$emit("error", "Nie udało się zapisać raportu", errorResponse);
      });
    },

    viewReport() {
      this.$router.openInNewTab({
        path: `/view/project/${this.projectId}/final-report`
      });
    },

    applyGenericForm(form) {
      if (!form) {
        this.$messagesBus.$emit("error", "Niezdefiniowany formularz generyczny");
        return;
      }

      let schema = genericFormUtil.getSectionSchemaFromSchema(form, 0, this.readonly);
      if (genericFormUtil.sectionSchemaContainsConditionalField(schema)) {
        let schemaValues = this.reportDataModel;
        this.formGenerator.currentSchema = genericFormUtil.filterSchemaByConditionalFieldsValue(schema, schemaValues);
        let indexes = genericFormUtil.calculateIndexes(schema);
        this.formGenerator.currentConditionalHashes = genericFormUtil.getConditionalValueHashes(schema, indexes);
      } else {
        this.formGenerator.currentSchema = schema;
      }
    },

    exportCosts() {
      let reportFinancialTableComponent = this.$refs.reportFinancialTable;
      let finalReportInvoicesComponent = this.$refs.finalReportInvoices;
      let financialTable = reportFinancialTableComponent.generateExport();
      let reportInvoices = finalReportInvoicesComponent.generateExport();
      let requestBody = {...financialTable, ...reportInvoices};

      FinalReport.postCostsExport(this, this.projectId, requestBody, successResponse => {
            let filename = "egranty_export_" + Dates.methods.getFilenameTimestamp() + "_raport.xlsx";
            this.$messagesBus.$emit("success", "Eksport danych wykonano do pliku " + filename, successResponse);
            Files.methods.downloadBlobResponse(successResponse, filename);
          },
          errorResponse => {
            this.$messagesBus.$emit("error", "Eksport nieudany", errorResponse);
          },
          {
            responseType: "blob",
          });
    },
  },

  mounted() {
    this.refresh();
  },
};
</script>
